import { notification } from 'antd';
import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import moment from 'moment-timezone';
import { getIntl, history, setLocale } from 'umi';

import {
  accountConfirm,
  authLogin,
  authLogout,
  authPartyLogin,
  authRefreshToken,
  sendTwoFactorAuthCode,
  validateSecurityCode,
  validateSsoClient,
} from '@/services/login';
import { authRedirect } from '@/utils/infrastructure/utilities';
import { getDefaultLocale } from '@/utils/infrastructure/utilities';
import {
  SESSION_TYPES,
  STORAGE_KEYS,
  clearAllUserSessions,
  getCurrentSession,
  setCurrentSession,
  setItem,
  getSessionItem,
  setSessionItem,
  removeSessionItem,
  getGlobalItem,
  setGlobalItem,
  removeGlobalItem,
  setAuthority,
} from '@/utils/infrastructure/auth';

const twoFactor = {
  required: false,
  codeSent: false,
  providers: [],
};

const initialState = {
  status: undefined,
  accountConfirmed: false,
  twoFactor,
  auth: false,
};

// Function to restore twoFactor state from localStorage
const restoreTwoFactorState = () => {
  try {
    const savedState = getSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
    if (savedState) return JSON.parse(savedState);
  } catch (e) {}
  return twoFactor;
};

// Function to save twoFactor state to localStorage
const saveTwoFactorState = (state) => {
  try {
    setSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE, JSON.stringify(state));
  } catch (e) {}
};

// Function to clear twoFactor state from localStorage
const clearTwoFactorState = () => {
  try {
    removeSessionItem(STORAGE_KEYS.TWO_FACTOR_STATE);
  } catch (e) {}
};

// Function to clear all SSO-related flags from localStorage
const clearSsoState = () => {
  try {
    localStorage.removeItem('sso-redirect-url');
    localStorage.removeItem('sso-client-id');
    localStorage.removeItem('sso-automatic-flow');
    localStorage.removeItem('sso-provider');
    localStorage.removeItem('sso-auto-disabled');
    localStorage.removeItem('sso-client-validated');
    localStorage.removeItem('sso-session-id');
  } catch (e) {}
};

const applyLocale = (payload) => {
  try {
    if (payload?.accessToken) {
      const locale = getDefaultLocale(payload);
      setLocale(locale, false);
      moment.locale(locale);
    }
  } catch (e) {}
};

const Model = {
  namespace: 'auth',
  state: {
    ...initialState,
    twoFactor: restoreTwoFactorState(),
  },
  effects: {
    *login({ payload }, { call, put, select }) {
      try {
        const token = yield call(authLogin, payload);

        // Check if this is an SSO response (status 206) with exchangeCode
        if (
          (token.status === 206 || token.code === '206' || token.status === 2) &&
          token.ssoRequired &&
          token.exchangeCode
        ) {
          const redirectUrl = localStorage.getItem('sso-redirect-url');

          if (redirectUrl) {
            clearSsoState();
            clearTwoFactorState();

            const finalUrl = `${redirectUrl}?code=${token.exchangeCode}`;
            window.location.href = finalUrl;
            return { success: true };
          }
        }

        if (payload.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(payload.accessToken),
          });
        }

        yield put({
          type: 'changeLoginStatus',
          payload: {
            ...token,
            ...(_.has(token, 'twoFactorRequired') && { ...payload }),
          },
        });

        yield put({ type: 'updateAuthorityAfterLogin', payload: token });
        applyLocale(token);

        authRedirect(token);

        return { success: true };
      } catch (e) {
        const portal = yield select((o) => o.companyPortal.portal);
        history.replace({
          pathname: '/user/login',
          ...(portal && { query: { portal: portal.id } }),
        });

        // Return a result indicating failure instead of throwing
        return { success: false, error: e };
      }
    },

    *validateSsoClient({ payload }, { call, put, select }) {
      try {
        yield call(validateSsoClient, payload);
        return { success: true };
      } catch (e) {
        return { success: false, error: e };
      }
    },

    *oauth2Login({ payload }, { call, put, select }) {
      try {
        const res = yield call(authPartyLogin, payload);
        const appLoginAppId = getGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
        removeGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);

        if (res.status === 206 || res.code === '206' || res.status === 2) {
          if (res.ssoRequired && res.exchangeCode) {
            const redirectUrl = localStorage.getItem('sso-redirect-url');

            if (redirectUrl) {
              clearSsoState();
              clearTwoFactorState();

              const finalUrl = `${redirectUrl}?code=${res.exchangeCode}`;
              window.location.href = finalUrl;
              return;
            }
          }
        }

        if (appLoginAppId && !res.accessToken) {
          setGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT, appLoginAppId);
        }

        yield put({
          type: 'changeLoginStatus',
          payload: { ...res, ...(appLoginAppId && { _appLoginAppId: appLoginAppId }) },
        });

        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);

        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');

        if (ssoRedirectUrl) {
          const ssoClientId = localStorage.getItem('sso-client-id');
          const ssoProvider = localStorage.getItem('sso-provider');

          const portalState = yield select((state) => state.companyPortal.portal);

          clearSsoState();
          clearTwoFactorState();

          try {
            const url = new URL(ssoRedirectUrl, window.location.origin);

            if (portalState) url.set('portal', portal.id);
            if (ssoClientId && !url.searchParams.get('clientId')) {
              url.searchParams.set('clientId', ssoClientId);
            }

            if (ssoProvider && !url.searchParams.get('provider')) {
              url.searchParams.set('provider', ssoProvider);
            }

            window.location.href = url.toString();
            return;
          } catch (e) {}
        }

        const appRedirectUrl = getGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);

        if (appRedirectUrl) {
          removeGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);
          window.location.href = appRedirectUrl;
          return;
        }

        window.location.href = history.location?.query?.redirect || '/';
      } catch (e) {
        removeGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);

        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');
        const ssoClientId = localStorage.getItem('sso-client-id');
        const ssoProvider = localStorage.getItem('sso-provider');

        localStorage.removeItem('sso-automatic-flow');
        localStorage.removeItem('sso-provider');
        removeGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);
        localStorage.setItem('sso-auto-disabled', 'true');

        const portal = yield select((state) => state.companyPortal.portal);
        const queryParams = [];

        if (portal) queryParams.push(`portal=${portal.id}`);
        if (ssoClientId) queryParams.push(`clientId=${encodeURIComponent(ssoClientId)}`);
        if (ssoRedirectUrl) queryParams.push(`redirectUrl=${encodeURIComponent(ssoRedirectUrl)}`);
        if (ssoProvider) queryParams.push(`provider=${encodeURIComponent(ssoProvider)}`);

        const query = queryParams.length ? `?${queryParams.join('&')}` : '';
        history.replace(`/user/login${query}`, history.location?.state);
      }
    },

    *sendTwoFactorCode({ payload }, { call, put, select }) {
      try {
        const data = yield select((st) => st.auth.twoFactor);
        // Only send the necessary data, avoiding circular references
        const requestData = {
          twoFactorProvider: payload.twoFactorProvider?.value || payload.twoFactorProvider,
          username: data.username,
          password: data.password,
        };

        yield call(sendTwoFactorAuthCode, requestData);
        const newTwoFactorState = { ...data, ...payload, codeSent: true };
        saveTwoFactorState(newTwoFactorState);

        yield put({ type: 'queryTwoFactorCode', payload: newTwoFactorState });
      } catch (e) {}
    },

    *validateCode({ payload }, { call, put, select }) {
      try {
        const data = yield select((st) => st.auth.twoFactor);
        const ssoClientId = localStorage.getItem('sso-client-id');
        const isSsoFlow = !!ssoClientId && !!localStorage.getItem('sso-redirect-url');

        const res = yield call(validateSecurityCode, {
          ...payload,
          ...data,
          ...(isSsoFlow && {
            isSsoFlow: true,
            ssoClientId,
          }),
        });

        // Check if this is an SSO response (status 206) with exchangeCode
        if (
          (res.status === 206 || res.code === '206' || res.status === 2) &&
          res.ssoRequired &&
          res.exchangeCode
        ) {
          const redirectUrl = localStorage.getItem('sso-redirect-url');

          if (redirectUrl) {
            clearSsoState();
            clearTwoFactorState();
            const finalUrl = `${redirectUrl}?code=${res.exchangeCode}`;
            window.location.href = finalUrl;
            return;
          }
        }

        yield put({ type: 'changeLoginStatus', payload: res });
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);

        if (res.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        }

        removeGlobalItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);

        const ssoRedirectUrl = localStorage.getItem('sso-redirect-url');

        if (ssoRedirectUrl) {
          const ssoClientId = localStorage.getItem('sso-client-id');
          const ssoProvider = localStorage.getItem('sso-provider');

          clearSsoState();
          clearTwoFactorState();

          try {
            const url = new URL(ssoRedirectUrl, window.location.origin);
            if (ssoClientId && !url.searchParams.get('clientId')) {
              url.searchParams.set('clientId', ssoClientId);
            }
            if (ssoProvider && !url.searchParams.get('provider')) {
              url.searchParams.set('provider', ssoProvider);
            }
            window.location.href = url.toString();
            return;
          } catch (e) {}
        }

        authRedirect(res);
      } catch (e) {}
    },

    *refreshToken({}, { call, put }) {
      try {
        const token = getSessionItem(STORAGE_KEYS.AUTH_TOKEN);
        const res = yield call(authRefreshToken, token);

        if (res.accessToken) {
          yield put({
            type: 'userContext/defaultContext',
            payload: jwtDecode(res.accessToken),
          });
        }

        yield put({ type: 'changeLoginStatus', payload: res });
        yield put({ type: 'updateAuthorityAfterLogin', payload: res });
        applyLocale(res);
        yield put({ type: 'user/fetchCurrent', payload: true });
      } catch (e) {
        // A failed refresh means the session can no longer be recovered. Swallowing
        // the error here used to leave the user on a dead session; force a logout so
        // they are redirected to login instead.
        yield put({ type: 'logout' });
      }
    },

    *updateAuthorityAfterLogin({ payload }, {}) {
      try {
        if (payload.accessToken) {
          const user = jwtDecode(payload.accessToken);
          setAuthority(user.role);
        }
      } catch (e) {}
    },

    *cleanupTwoFactor(o, { put }) {
      clearTwoFactorState();
      yield put({ type: 'cleanTwoFactor' });
    },

    *cleanConfirmAccount(o, { put }) {
      yield put({ type: 'cleanAccountConfirm' });
    },

    *confirmAccount({ payload }, { call, put, select }) {
      try {
        const res = yield call(accountConfirm, payload);
        if (res?.message) {
          notification.success({
            message: getIntl().formatMessage({ id: 'user.twoFactor.alert.title' }),
            description: res.message,
          });
        }
        yield put({ type: 'queryConfirm', payload: true });
      } catch (e) {
        const portal = yield select((state) => state.companyPortal.portal);
        history.replace(
          {
            pathname: '/user/login',
            ...(portal && {
              query: { portal: portal.id },
            }),
          },
          history.location?.state || portal,
        );
      }
    },

    *logout(o, { call, put, select }) {
      if (window.location.pathname !== '/user/login') {
        sessionStorage.setItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS, 'true');
        try {
          yield call(authLogout);
          yield put({ type: 'auth/cleanup' });
          yield put({ type: 'user/clearCurrentUser' });

          // Read portal from DVA state BEFORE clearing session
          const portalState = yield select((state) => state.companyPortal.portal);
          const currentSession = getCurrentSession();
          const userId = currentSession?.userId;

          // Clear ALL sessions for this user (AUTOMY + PORTAL + APP)
          if (userId) {
            clearAllUserSessions(userId);
          } else {
            removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
            removeSessionItem(STORAGE_KEYS.AUTH_USER);
            removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
            removeSessionItem(STORAGE_KEYS.AUTHORITY);
            removeSessionItem(STORAGE_KEYS.USER_TIMEZONE);
          }

          clearTwoFactorState();
          setCurrentSession(null);

          const searchParams = new URLSearchParams();

          if (portalState) {
            searchParams.set('portal', portalState.id);
            yield put({
              type: 'companyPortal/auth',
              payload: _.pick(portalState, ['companyCode', 'portalCode']),
            });
          }

          if (window.location.pathname !== '/') {
            searchParams.set('redirect', window.location.href);
          }

          const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
          history.replace(`/user/login${query}`, history.location.state);
        } finally {
          sessionStorage.removeItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS);
        }
      }
    },

    *cleanup(o, { put }) {
      yield put({ type: 'reset' });
    },
  },
  reducers: {
    changeLoginStatus(state, { payload }) {
      try {
        if (payload.accessToken) {
          const user = jwtDecode(payload.accessToken);
          const userId = user.primarysid;

          const appLoginId =
            payload._appLoginAppId || getGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
          removeGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);

          const { _appLoginAppId, ...tokenPayload } = payload;
          const tokenData = JSON.stringify({ ...tokenPayload, issuedAt: Date.now() });

          if (appLoginId && userId) {
            setItem(
              SESSION_TYPES.APP,
              userId,
              String(appLoginId),
              STORAGE_KEYS.AUTH_TOKEN,
              tokenData,
            );
            setItem(
              SESSION_TYPES.APP,
              userId,
              String(appLoginId),
              STORAGE_KEYS.USER_TIMEZONE,
              user.timeZone,
            );
            setCurrentSession({
              type: SESSION_TYPES.APP,
              userId,
              resourceId: String(appLoginId),
            });
          } else {
            let session = getCurrentSession();
            if (!session) {
              try {
                const portalConfigRaw = sessionStorage.getItem(STORAGE_KEYS.PORTAL_CONFIG);
                if (portalConfigRaw) {
                  const portalConfig = JSON.parse(portalConfigRaw);
                  if (portalConfig?.id) {
                    session = {
                      type: SESSION_TYPES.PORTAL,
                      userId,
                      resourceId: String(portalConfig.id),
                    };
                  }
                }
              } catch (e) {}

              if (!session) {
                session = { type: SESSION_TYPES.AUTOMY, userId, resourceId: null };
              }

              setCurrentSession(session);
              if (session.type === SESSION_TYPES.PORTAL) {
                try {
                  const pendingConfig = sessionStorage.getItem(STORAGE_KEYS.PORTAL_CONFIG);
                  if (pendingConfig) {
                    setItem(
                      SESSION_TYPES.PORTAL,
                      session.userId,
                      session.resourceId,
                      STORAGE_KEYS.PORTAL_CONFIG,
                      pendingConfig,
                    );
                    sessionStorage.removeItem(STORAGE_KEYS.PORTAL_CONFIG);
                  }
                } catch (e) {}
              }
            }

            setItem(
              session.type,
              session.userId,
              session.resourceId,
              STORAGE_KEYS.AUTH_TOKEN,
              tokenData,
            );
            setItem(
              session.type,
              session.userId,
              session.resourceId,
              STORAGE_KEYS.USER_TIMEZONE,
              user.timeZone,
            );
          }

          moment.tz.setDefault(user.timeZone);

          setAuthority(user.role);
        }
      } catch (e) {}

      const newState = {
        ...state,
        status: payload.status,
        auth: !!payload.accessToken,
        type: payload.type,
        ...(payload.twoFactorRequired && {
          twoFactor: {
            ...state.twoFactor,
            required: payload.twoFactorRequired,
            providers: _.map(payload.twoFactorProviders, (str) => ({
              label: `user.twoFactor.provider.${str}`,
              value: str,
            })),
            ..._.pick(payload, ['password', 'username']),
          },
        }),
        ...(payload.accessToken && { twoFactor }),
      };

      // Save twoFactor state if required
      if (payload.twoFactorRequired) saveTwoFactorState(newState.twoFactor);
      else if (payload.accessToken) clearTwoFactorState();
      return newState;
    },

    queryTwoFactorCode(state, { payload }) {
      const newTwoFactorState = { ...state.twoFactor, ...payload };
      saveTwoFactorState(newTwoFactorState);
      return {
        ...state,
        twoFactor: newTwoFactorState,
      };
    },

    queryConfirm(state, { payload }) {
      return {
        ...state,
        accountConfirmed: payload,
      };
    },

    cleanTwoFactor(state) {
      clearTwoFactorState();
      return {
        ...state,
        twoFactor,
      };
    },

    cleanAccountConfirm(state) {
      return {
        ...state,
        accountConfirmed: false,
      };
    },

    reset() {
      clearTwoFactorState();
      setCurrentSession(null);
      return initialState;
    },
  },
};

export default Model;
